<script setup lang="ts">
import { computed, defineProps } from 'vue'
import { usePlanetStore } from '@/stores/planetStore'
import { useAssetStore } from '@/stores/assetStore'
import { useGameStore } from '@/stores/gameStore.ts'
import { CDN } from '@/models/cdn.ts'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import BuildingRequired from '@/components/Sections/Planets/PlanetTabs/Generic/BuildingRequired.vue'
import TechRequired from '@/components/Sections/Planets/PlanetTabs/Generic/TechRequired.vue'
import GenericIconWithColoredNumber from '@/components/Elements/Images/Icons/GenericIconWithColoredNumber.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import StyledImage from '@/components/Elements/Images/StyledImage.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Props
 */
const props = defineProps({
  planetId: {
    type: Number,
    required: true
  }
})

/**
 * Stores / models
 */
const planetStore = usePlanetStore()
const assetStore = useAssetStore()
const gameStore = useGameStore()

/**
 * Computed
 */
const planetData = computed(() => planetStore.findPlanetById(props.planetId)!)

const dysonSwarmTechId = computed(() => {
  return assetStore.findTechByCode('dyson_swarm').id
})

const status = computed(() => {
  // Check if we have invented the correct tech
  const tech = assetStore.findTechByCode('dyson_swarm')
  if (!gameStore.hasPlayerTech(tech.id)) {
    return 'need-tech'
  }
  // check if we have the correct buildings
  if (!planetStore.hasBuilding(props.planetId, 'dyson_sphere_construction_facility')) {
    return 'need-building'
  }
  // show the progress
  return 'constructing'
})

const requiredOre = computed(() => {
  return assetStore.findOreById(gameStore.player!.dysonSwarmOreId)
})
</script>

<template>
  <StandardCard :title="t('planets.dysonSwarm.title')" top-padding="2">
    <div class="text-subtext mb-2">
      {{ t('planets.dysonSwarm.helpPart1') }} Each part requires 1
      <GenericIcon
        :type="'ore' + gameStore.player?.dysonSwarmOreId"
        :tooltip="requiredOre?.oreName"
      />
      {{ requiredOre?.oreName }} {{ t('planets.dysonSwarm.helpPart2') }} 100
      <GenericIcon type="energy" tooltip="Energy" /> {{ t('planets.dysonSwarm.helpPart3') }}
    </div>

    <div
      v-if="status === 'constructing'"
      class="FSimpleCard grid xl:grid-cols-5 lg:grid-cols-3 grid-cols-2 mt-1 gap-2"
    >
      <div v-for="index in 5" :key="index" class="flex flex-col items-center justify-center">
        <div class="relative">
          <StyledImage
            :src="CDN.interface(256, 'dyson_swarm')"
            :grayscale="planetData.dysonSwarmsBuilt < index"
            :class="planetData.dysonSwarmsBuilt < index ? 'opacity-50' : ''"
            size="full"
          />
          <div
            class="absolute bottom-0 left-0 right-0 bg-black/60 py-1 text-center rounded-b-md flex justify-center"
          >
            <template v-if="planetData.dysonSwarmsBuilt >= index">
              <GenericIconWithColoredNumber :amount="100" type="energy" />
            </template>
            <template v-else>
              <GenericIconWithColoredNumber
                :amount="1"
                :type="'ore' + gameStore.player?.dysonSwarmOreId"
              />
            </template>
          </div>
        </div>
      </div>
    </div>
  </StandardCard>
  <StandardCard top-padding="1">
    <TechRequired
      v-if="status === 'need-tech'"
      :techId="dysonSwarmTechId"
      :imageCode="'dyson_swarm'"
      :text="t('planets.dysonSwarm.needTech')"
    />
    <BuildingRequired
      v-else-if="status === 'need-building'"
      :buildingCode="'dyson_sphere_construction_facility'"
      :text="t('planets.dysonSwarm.needBuilding')"
    />
  </StandardCard>
</template>

<style scoped lang="scss"></style>
